import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";

import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as String from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Utils from "../utils/utils";

import { CalendarOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import styles from "./RentInfo.module.css";
import PageContainer from "../layout/PageContainer";
import { Affix, Anchor, Badge, Button, Col, Modal, Row, Space } from "antd";
import RentDetail from "../components/RentDetail";
import RentUserDetail from "../components/RentUserDetail";
import RentCarDetail from "../components/RentCarDetail";
import moment from "moment";
import RentPaymentDetail from "../components/RentPaymentDetail";

function RentInfo() {
    const navigate = useNavigate();
    const params = useParams();
    const [currentRentId, setCurrentRentId] = useState<number>();
    const [rent, setRent] = useState<Types.RentDto>();
    const [paymentItems, setPaymentItems] = useState<Array<Types.RentPaymentItemDto>>([]);
    const [openModalCancelReservation, setOpenModalCancelReservation] = useState(false);
    const [cancelRent, setCancelRent] = useState<Types.RentDto>();
    const [arrivedRent, setArrivedRent] = useState<Types.RentDto>();

    useEffect(() => {
        if (params.rentId) setCurrentRentId(Number(params.rentId));
    }, [params.rentId]);

    useEffect(() => {
        currentRentId && requestGetPaymentItems();
        currentRentId && requestGetRent();
    }, [currentRentId]);

    const onRefreshRentData = () => {
        currentRentId && requestGetRent();
    };

    const requestAxiosGetPaymentItems = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_PAYMENT_ITEMS_URL);
        return response.data;
    };

    const {
        loading: loadingGetPaymentItems,
        error: errorGetPaymentItems,
        data: resultGetPaymentItems,
        execute: requestGetPaymentItems,
    } = useAsyncAxios(requestAxiosGetPaymentItems);

    useEffect(() => {
        if (resultGetPaymentItems === null) return;
        console.log("resultGetPaymentItems", resultGetPaymentItems);
        setPaymentItems(resultGetPaymentItems.rentPaymentItems);
    }, [resultGetPaymentItems]);

    useEffect(() => {
        if (errorGetPaymentItems === null) return;
        console.log("errorGetPaymentItems", errorGetPaymentItems);
    }, [errorGetPaymentItems]);

    const requestAxiosGetRent = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_URL + currentRentId);
        return response.data;
    };

    const { loading: loadingGetRent, error: errorGetRent, data: resultGetRent, execute: requestGetRent } = useAsyncAxios(requestAxiosGetRent);

    useEffect(() => {
        if (resultGetRent === null) return;
        console.log("resultGetRent", resultGetRent);
        setRent(resultGetRent.rent);
    }, [resultGetRent]);

    useEffect(() => {
        if (errorGetRent === null) return;
        console.log("errorGetRent", errorGetRent);
    }, [errorGetRent]);

    const addBatteryPayment = (rentInfo: Types.RentDto, ignoreExistPayment?: boolean) => {
        if (
            ignoreExistPayment !== true &&
            rentInfo.rentPayments.find((rentPayment) => rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("배터리 미충전"))
        ) {
            console.log("penalty battery already exist");
            return false;
        }

        if (rentInfo.startBatterySoc && rentInfo.endBatterySoc) {
            if (rentInfo.endBatterySoc >= Common.BATTERY_MAX_LIMIT) return false;

            // const startSoc = rentInfo.startBatterySoc > Common.BATTERY_MAX_LIMIT ? Common.BATTERY_MAX_LIMIT : rentInfo.startBatterySoc;
            const startSoc = rentInfo.startBatterySoc;

            if (startSoc - rentInfo.endBatterySoc >= 1) {
                const batteryPaymentItem = paymentItems.find((paymentItem) =>
                    paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("배터리 미충전")
                );
                if (batteryPaymentItem === null || batteryPaymentItem === undefined) return false;

                const penaltySoc = Math.floor(startSoc - rentInfo.endBatterySoc);
                const payment: any = {
                    rent: rentInfo,
                    rentPaymentType: "PENALTY",
                    rentPaymentItem: batteryPaymentItem,
                    basicPrice: penaltySoc * Common.BATTERY_PENALTY,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: penaltySoc * Common.BATTERY_PENALTY,
                };

                requestAddBatteryPayment(payment);
                return true;
            }
        }

        return false;
    };

    const requestAxiosAddBatteryPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddBatteryPayment,
        error: errorAddBatteryPayment,
        data: resultAddBatteryPayment,
        execute: requestAddBatteryPayment,
    } = useAsyncAxios(requestAxiosAddBatteryPayment);

    useEffect(() => {
        if (resultAddBatteryPayment === null) return;
        console.log("resultAddBatteryPayment", resultAddBatteryPayment);
        // arrivedRent && addDelayedArrivePayment(arrivedRent);
        onRefreshRentData();
    }, [resultAddBatteryPayment]);

    useEffect(() => {
        if (errorAddBatteryPayment === null) return;
        console.log("errorAddBatteryPayment", errorAddBatteryPayment);
    }, [errorAddBatteryPayment]);

    const addDelayedArrivePayment = (rentInfo: Types.RentDto) => {
        if (rentInfo.rentPayments.find((rentPayment) => rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("반납 지연"))) {
            console.log("penalty delay arrive already exist");
            return false;
        }

        if (rentInfo.arrivedDate !== null && rentInfo.arrivedDate !== undefined && rentInfo.usedDate !== null && rentInfo.usedDate !== undefined) {
            const originalArrivedDate = moment(rentInfo.endDate + Common.DEFAULT_ARRIVED_TIME);
            const realArrivedDate = moment(Utils.utcToLocalTime(rentInfo.arrivedDate, Common.FORMAT_DATE_TIME));

            const minutes = realArrivedDate.diff(originalArrivedDate, "minutes");

            let delayedPaymentItem = null;
            if (minutes > 0 && minutes <= 60) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 이내 지연 시")
                );
            } else if (minutes > 60 && minutes <= 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 초과 2시간 내 지연 시")
                );
            } else if (minutes > 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("2시간 초과 지연 시")
                );
            }

            if (delayedPaymentItem === null || delayedPaymentItem === undefined) return false;

            const payment: any = {
                rent: rentInfo,
                rentPaymentType: "PENALTY",
                rentPaymentItem: delayedPaymentItem,
                basicPrice: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
                paymentMethod: "OFFLINE_BANK",
                adjustedPrice: 0,
                amount: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
            };

            requestAddDelayedArrivePayment(payment);

            return true;
        }

        return false;
    };

    const requestAxiosAddDelayedArrivePayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddDelayedArrivePayment,
        error: errorAddDelayedArrivePayment,
        data: resultAddDelayedArrivePayment,
        execute: requestAddDelayedArrivePayment,
    } = useAsyncAxios(requestAxiosAddDelayedArrivePayment);

    useEffect(() => {
        if (resultAddDelayedArrivePayment === null) return;
        console.log("resultAddDelayedArrivePayment", resultAddDelayedArrivePayment);
        onRefreshRentData();
    }, [resultAddDelayedArrivePayment]);

    useEffect(() => {
        if (errorAddDelayedArrivePayment === null) return;
        console.log("errorAddDelayedArrivePayment", errorAddDelayedArrivePayment);
    }, [errorAddDelayedArrivePayment]);

    const getPayment = (rent: Types.RentDto) => {
        return rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "RENT");
    };

    const getCancelPercent = (startDate: string) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return 100 - percent;
        }
        return 100;
    };

    const getCancelAmount = (startDate: string, rentalPrice: number) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return (rentalPrice / 100) * (100 - percent);
        }
        return (rentalPrice / 100) * 100;
    };

    const handleCancelReservationModelOk = () => {
        setOpenModalCancelReservation(false);

        if (cancelRent !== undefined) {
            const insuranceAmount = cancelRent?.rentInsurance?.price || 0;
            const paidItemAmount = cancelRent?.rentPaidItems?.reduce((total, item) => total + item.price, 0) || 0;
            const cancelAmount = getCancelAmount(cancelRent.startDate, (getPayment(cancelRent)?.amount || 0) - insuranceAmount - paidItemAmount);

            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            requestCancelPayment(getPayment(cancelRent)?.rentPaymentId, returnAmount);
        }
    };

    const requestAxiosCancelPayment = async (rentPaymentId: number, cancelAmount: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_CANCEL_URL + rentPaymentId + "?cancelAmount=" + cancelAmount);
        return response.data;
    };

    const {
        loading: loadingCancelPayment,
        error: errorCancelPayment,
        data: resultCancelPayment,
        execute: requestCancelPayment,
    } = useAsyncAxios(requestAxiosCancelPayment);

    useEffect(() => {
        if (resultCancelPayment === null) return;
        console.log("resultCancelPayment", resultCancelPayment);

        requestCancelRent(resultCancelPayment.rentPayment.rent.rentId);
    }, [resultCancelPayment]);

    useEffect(() => {
        if (errorCancelPayment === null) return;
        console.log("errorPaymentCarComplete", errorCancelPayment);
    }, [errorCancelPayment]);

    const onCancelRent = (rent: Types.RentDto) => {
        setOpenModalCancelReservation(true);
        setCancelRent(rent);
    };

    const requestAxiosCancelRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_URL + rentId + Request.RENT_CANCEL_URL);
        return response.data;
    };

    const { loading: loadingCancelRent, error: errorCancelRent, data: resultCancelRent, execute: requestCancelRent } = useAsyncAxios(requestAxiosCancelRent);

    useEffect(() => {
        if (resultCancelRent === null) return;
        console.log("resultCancelRent", resultCancelRent);

        onRefreshRentData();
    }, [resultCancelRent]);

    useEffect(() => {
        if (errorCancelRent === null) return;
        console.log("errorCancelRent", errorCancelRent);
    }, [errorCancelRent]);

    const displayReturnPayment = () => {
        if (cancelRent) {
            const insuranceAmount = cancelRent?.rentInsurance?.price || 0;
            const paidItemAmount = cancelRent?.rentPaidItems?.reduce((total, item) => total + item.price, 0) || 0;
            const cancelAmount = getCancelAmount(cancelRent.startDate, (getPayment(cancelRent)?.amount || 0) - insuranceAmount - paidItemAmount);

            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            return (
                <>
                    <p>결제금액 : {Utils.convertCurrencyKorean(getPayment(cancelRent)?.amount || 0)}</p>
                    <p>
                        위약금 : {Utils.convertCurrencyKorean(cancelAmount)} &#40;{getCancelPercent(cancelRent.startDate)} %&#41;
                    </p>
                    <p>환불금액 : {Utils.convertCurrencyKorean(returnAmount)}</p>
                </>
            );
        } else return null;
    };

    const showUseRentConfirm = (rent: Types.RentDto) => {
        Utils.addModalStack();
        Modal.confirm({
            title: "출차",
            content: "출차 하시겠습니까?",
            okText: "확인",
            onOk() {
                Utils.popModalStack();
                onUseRent(rent);
            },
            cancelText: "취소",
            onCancel() {
                Utils.popModalStack();
            },
            centered: true,
        });
    };

    const onUseRent = (rent: Types.RentDto) => {
        requestUseRent(rent.rentId);
    };

    const requestAxiosUseRent = async () => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + currentRentId + Request.RENT_USE_URL);
        return response.data;
    };

    const { loading: loadingUseRent, error: errorUseRent, data: resultUseRent, execute: requestUseRent } = useAsyncAxios(requestAxiosUseRent);

    useEffect(() => {
        if (resultUseRent === null) return;
        console.log("resultUseRent", resultUseRent);
        onRefreshRentData();
    }, [resultUseRent]);

    useEffect(() => {
        if (errorUseRent === null) return;
        console.log("errorUseRent", errorUseRent);
    }, [errorUseRent]);

    const showCancelUseRentConfirm = (rent: Types.RentDto) => {
        Utils.addModalStack();
        Modal.confirm({
            title: "출차 취소",
            content: "출차를 취소 하시겠습니까?",
            okText: "확인",
            onOk() {
                Utils.popModalStack();
                onCancelUseRent(rent);
            },
            cancelText: "취소",
            onCancel() {
                Utils.popModalStack();
            },
            centered: true,
        });
    };

    const onCancelUseRent = (rent: Types.RentDto) => {
        requestCancelUseRent(rent.rentId);
    };

    const requestAxiosCancelUseRent = async () => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + currentRentId + Request.RENT_CANCEL_USE_URL);
        return response.data;
    };

    const {
        loading: loadingCancelUseRent,
        error: errorCancelUseRent,
        data: resultCancelUseRent,
        execute: requestCancelUseRent,
    } = useAsyncAxios(requestAxiosCancelUseRent);

    useEffect(() => {
        if (resultCancelUseRent === null) return;
        console.log("resultCancelUseRent", resultCancelUseRent);
        onRefreshRentData();
    }, [resultCancelUseRent]);

    useEffect(() => {
        if (errorCancelUseRent === null) return;
        console.log("errorCancelUseRent", errorCancelUseRent);
    }, [errorCancelUseRent]);

    const requestAxiosReturnRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_RETURN_URL);
        return response.data;
    };

    const { loading: loadingReturnRent, error: errorReturnRent, data: resultReturnRent, execute: requestReturnRent } = useAsyncAxios(requestAxiosReturnRent);

    useEffect(() => {
        if (resultReturnRent === null) return;
        console.log("resultReturnRent", resultReturnRent);

        onRefreshRentData();
    }, [resultReturnRent]);

    useEffect(() => {
        if (errorReturnRent === null) return;
        console.log("errorReturnRent", errorReturnRent);
    }, [errorReturnRent]);

    const onArrivedRent = (rent: Types.RentDto) => {
        Utils.addModalStack();
        Modal.confirm({
            title: "반납",
            content: "반납 처리 하시겠습니까? 반납 후 정산을 완료해 주세요.",
            okText: "확인",
            onOk() {
                Utils.popModalStack();
                requestArrivedRent(rent.rentId);
            },
            cancelText: "취소",
            onCancel() {
                Utils.popModalStack();
            },
            centered: true,
        });
    };

    const requestAxiosArrivedRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_ARRIVE_URL);
        return response.data;
    };

    const {
        loading: loadingArrivedRent,
        error: errorArrivedRent,
        data: resultArrivedRent,
        execute: requestArrivedRent,
    } = useAsyncAxios(requestAxiosArrivedRent);

    useEffect(() => {
        if (resultArrivedRent === null) return;
        console.log("resultArrivedRent", resultArrivedRent);
        setArrivedRent(resultArrivedRent.rent);
        requestReturnRent(resultArrivedRent.rent.rentId);
        onRefreshRentData();
    }, [resultArrivedRent]);

    useEffect(() => {
        if (errorArrivedRent === null) return;
        console.log("errorArrivedRent", errorArrivedRent);
    }, [errorArrivedRent]);

    useEffect(() => {
        if (arrivedRent) {
            addBatteryPayment(arrivedRent);
            addDelayedArrivePayment(arrivedRent);
            // if (addBatteryPayment(arrivedRent) === false) addDelayedArrivePayment(arrivedRent);
        }
    }, [arrivedRent]);

    const onChangeRentCar = (rent: Types.RentDto, rentCarId: number) => {
        requestChangeRentCar(rent.rentId, rentCarId);
    };

    const requestAxiosChangeRentCar = async (rentId: number, rentCarId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { usedRentCar: { rentCarId: rentCarId } });
        return response.data;
    };

    const {
        loading: loadingChangeRentCar,
        error: errorChangeRentCar,
        data: resultChangeRentCar,
        execute: requestChangeRentCar,
    } = useAsyncAxios(requestAxiosChangeRentCar);

    useEffect(() => {
        if (resultChangeRentCar === null) return;
        console.log("resultChangeRentCar", resultChangeRentCar);
        onRefreshRentData();
    }, [resultChangeRentCar]);

    useEffect(() => {
        if (errorChangeRentCar === null) return;
        console.log("errorChangeRentCar", errorChangeRentCar);
    }, [errorChangeRentCar]);

    const onChangeRentMemo = (rent: Types.RentDto, memo: string) => {
        requestChangeRentMemo(rent.rentId, memo);
    };

    const requestAxiosChangeRentMemo = async (rentId: number, memo: string) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { memo: memo });
        return response.data;
    };

    const {
        loading: loadingChangeRentMemo,
        error: errorChangeRentMemo,
        data: resultChangeRentMemo,
        execute: requestChangeRentMemo,
    } = useAsyncAxios(requestAxiosChangeRentMemo);

    useEffect(() => {
        if (resultChangeRentMemo === null) return;
        console.log("resultChangeRentMemo", resultChangeRentMemo);
        onRefreshRentData();
    }, [resultChangeRentMemo]);

    useEffect(() => {
        if (errorChangeRentMemo === null) return;
        console.log("errorChangeRentMemo", errorChangeRentMemo);
    }, [errorChangeRentMemo]);

    const onUpdatePaidItem = (rent: Types.RentDto, paidItemIds: Array<number>, payments?: Array<Types.RentPaidItemDto>) => {
        requestUpdatePaidItem(rent.rentId, paidItemIds);

        const tablePayment = rent?.rentPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("테이블"));
        const beddingPayment = rent?.rentPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("침구"));

        payments?.forEach((item: Types.RentPaidItemDto) => {
            if ((item?.rentPaidItemType === "TABLE_CHAIR" && tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && beddingPayment)) {
                const payment: any = {
                    rentPaymentId: item?.rentPaidItemType === "TABLE_CHAIR" ? tablePayment?.rentPaymentId : beddingPayment?.rentPaymentId,
                    basicPrice: item?.price,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestUpdatePaidItemPayment(payment);
            } else if ((item?.rentPaidItemType === "TABLE_CHAIR" && !tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && !beddingPayment)) {
                const payment: any = {
                    rent: rent,
                    rentPaymentType: "PAID_ITEM",
                    basicPrice: item?.price,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestAddPaidItemPayment(payment);
            }
        });
    };

    const requestAxiosUpdatePaidItem = async (rentId: number, paidItemIds: Array<number>) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            rentPaidItems: paidItemIds.map((paidItemId) => {
                return { rentPaidItemId: paidItemId };
            }),
        });
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItem,
        error: errorUpdatePaidItem,
        data: resultUpdatePaidItem,
        execute: requestUpdatePaidItem,
    } = useAsyncAxios(requestAxiosUpdatePaidItem);

    useEffect(() => {
        if (resultUpdatePaidItem === null) return;
        console.log("resultUpdatePaidItem", resultUpdatePaidItem);
        onRefreshRentData();

        const paidItems = resultUpdatePaidItem?.rent?.rentPaidItems;
        const paidItemsPayments = resultUpdatePaidItem?.rent?.rentPayments.filter((payment: Types.RentPaymentDto) => payment?.rentPaymentType === "PAID_ITEM");
        const tableItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "TABLE_CHAIR");
        const beddingItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "BEDDING_SET");
        const tablePayment = paidItemsPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("테이블"));
        const beddingPayment = paidItemsPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("침구"));

        if (!tableItem && tablePayment) {
            requestDeletePayment(tablePayment);
        }
        if (!beddingItem && beddingPayment) {
            requestDeletePayment(beddingPayment);
        }
    }, [resultUpdatePaidItem]);

    useEffect(() => {
        if (errorUpdatePaidItem === null) return;
        console.log("errorUpdatePaidItem", errorUpdatePaidItem);
    }, [errorUpdatePaidItem]);

    const requestAxiosAddPaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddPaidItemPayment,
        error: errorAddPaidItemPayment,
        data: resultAddPaidItemPayment,
        execute: requestAddPaidItemPayment,
    } = useAsyncAxios(requestAxiosAddPaidItemPayment);

    useEffect(() => {
        if (resultAddPaidItemPayment === null) return;
        console.log("resultAddPaidItemPayment", resultAddPaidItemPayment);
        onRefreshRentData();
    }, [resultAddPaidItemPayment]);

    useEffect(() => {
        if (errorAddPaidItemPayment === null) return;
        console.log("errorAddPaidItemPayment", errorAddPaidItemPayment);
    }, [errorAddPaidItemPayment]);

    const requestAxiosUpdatePaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_PAYMENT_URL + payment.rentPaymentId, payment);
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItemPayment,
        error: errorUpdatePaidItemPayment,
        data: resultUpdatePaidItemPayment,
        execute: requestUpdatePaidItemPayment,
    } = useAsyncAxios(requestAxiosUpdatePaidItemPayment);

    useEffect(() => {
        if (resultUpdatePaidItemPayment === null) return;
        console.log("resultUpdatePaidItemPayment", resultUpdatePaidItemPayment);
        onRefreshRentData();
    }, [resultUpdatePaidItemPayment]);

    useEffect(() => {
        if (errorUpdatePaidItemPayment === null) return;
        console.log("errorUpdatePaidItemPayment", errorUpdatePaidItemPayment);
    }, [errorUpdatePaidItemPayment]);

    const goToUseProcess = () => {
        navigate(Common.PAGE_USE_CHECKLIST + "/" + currentRentId);
    };

    const onChangeRentBattery = (rent: Types.RentDto, startSoc: number | null, endSoc: number | null) => {
        console.log("onChangeRentBattery", rent, startSoc, endSoc);
        requestChangeRentBattery(rent.rentId, startSoc, endSoc);
    };

    const requestAxiosChangeRentBattery = async (rentId: number, startSoc: number | null, endSoc: number | null) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { startBatterySoc: startSoc, endBatterySoc: endSoc });
        return response.data;
    };

    const {
        loading: loadingChangeRentBattery,
        error: errorChangeRentBattery,
        data: resultChangeRentBattery,
        execute: requestChangeRentBattery,
    } = useAsyncAxios(requestAxiosChangeRentBattery);

    const [batteryRent, setBatteryRent] = useState<Types.RentDto>();

    useEffect(() => {
        if (resultChangeRentBattery === null) return;
        console.log("resultChangeRentBattery", resultChangeRentBattery);

        const rentInfo = resultChangeRentBattery.rent as Types.RentDto;
        setBatteryRent(rentInfo);
        const batteryPayment = rentInfo.rentPayments.find((rentPayment) =>
            rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("배터리 미충전")
        );

        if (batteryPayment) {
            requestDeletePayment(batteryPayment);
        } else {
            addBatteryPayment(rentInfo);
        }

        onRefreshRentData();
    }, [resultChangeRentBattery]);

    useEffect(() => {
        if (errorChangeRentBattery === null) return;
        console.log("errorChangeRentBattery", errorChangeRentBattery);
    }, [errorChangeRentBattery]);

    const requestAxiosDeletePayment = async (rentPayment: Types.RentPaymentDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_PAYMENT_URL + rentPayment.rentPaymentId);
        return response.data;
    };

    const {
        loading: loadingDeletePayment,
        error: errorDeletePayment,
        data: resultDeletePayment,
        execute: requestDeletePayment,
    } = useAsyncAxios(requestAxiosDeletePayment);

    useEffect(() => {
        if (resultDeletePayment === null) return;
        console.log("resultDeletePayment", resultDeletePayment);
        batteryRent && addBatteryPayment(batteryRent, true);
        onRefreshRentData();
    }, [resultDeletePayment]);

    useEffect(() => {
        if (errorDeletePayment === null) return;
        console.log("errorDeletePayment", errorDeletePayment);
    }, [errorDeletePayment]);

    const goToReturnProcess = () => {
        // navigate(Common.PAGE_RETURN_BATTERY + "/" + currentRentId);  // skip
        navigate(Common.PAGE_RETURN_CHECKLIST + "/" + currentRentId);
    };

    return (
        <PageContainer>
            <Anchor
                className="custom-anchor"
                direction="horizontal"
                items={[
                    {
                        key: "rent_detail",
                        href: "#rent_detail",
                        title: "예약정보",
                    },
                    {
                        key: "user_detail",
                        href: "#user_detail",
                        title: "예약자정보",
                    },
                    {
                        key: "car_detail",
                        href: "#car_detail",
                        title: "차량상태",
                    },
                    {
                        key: "payment_detail",
                        href: "#payment_detail",
                        title: rent?.rentPayments.find((payment) => payment.paidDate === null) ? (
                            <Badge dot offset={[3, 0]}>
                                <span style={{ color: "var(--danger-color)", fontSize: 16 }}>정산정보</span>
                            </Badge>
                        ) : (
                            "정산정보"
                        ),
                    },
                ]}
            />
            <Space direction="vertical" size={24} style={{ width: "calc(100% - 32px)", padding: "54px 16px 16px 16px", paddingBottom: 16 }}>
                <Row gutter={10} style={{ width: "100%", margin: 0 }}>
                    <Col xs={12} className="gutter-row" style={{ paddingLeft: 0 }}>
                        <Row className={styles.box}>
                            <CalendarOutlined />
                            <span style={{ marginLeft: 2 }}>출차일</span>
                            <p className={styles.highlightText}>{Utils.convertDateString(rent?.startDate, Common.FORMAT_SIMPLE_DATE)}</p>
                            <Row gutter={8} style={{ width: "100%", margin: 0 }}>
                                <Col xs={12} className="gutter-row" style={{ paddingLeft: 0 }}>
                                    <Button
                                        disabled={rent?.usedDate ? true : false}
                                        style={{ width: "100%" }}
                                        onClick={() => rent && showUseRentConfirm(rent) /*goToUseProcess()*/}
                                    >
                                        {String.rent_use}
                                    </Button>
                                </Col>
                                <Col xs={12} className="gutter-row" style={{ paddingRight: 0 }}>
                                    <Button
                                        disabled={rent?.usedDate && !rent?.arrivedDate ? false : true}
                                        style={{ width: "100%" }}
                                        onClick={() => rent && showCancelUseRentConfirm(rent)}
                                    >
                                        {String.rent_cancel_use}
                                    </Button>
                                </Col>
                            </Row>
                        </Row>
                    </Col>
                    <Col xs={12} className="gutter-row" style={{ paddingRight: 0 }}>
                        <Row className={styles.box}>
                            <CalendarOutlined />
                            <span style={{ marginLeft: 2 }}>반납일</span>
                            <p className={styles.highlightText}>{Utils.convertDateString(rent?.endDate, Common.FORMAT_SIMPLE_DATE)}</p>
                            <Button
                                disabled={rent?.arrivedDate && rent?.returnDate ? true : false}
                                style={{ width: "100%" }}
                                onClick={() => rent && onArrivedRent(rent)}
                            >
                                {String.rent_return}
                            </Button>
                        </Row>
                    </Col>
                </Row>
                <Space id="rent_detail" direction="vertical" size={10} style={{ width: "100%" }}>
                    {rent && (
                        <RentDetail
                            rent={rent}
                            onChangeRentMemo={onChangeRentMemo}
                            onUpdatePaidItem={onUpdatePaidItem}
                            onChangeRentCar={(rent: Types.RentDto, rentCarId: number) => onChangeRentCar(rent, rentCarId)}
                        />
                    )}
                </Space>
                <Space id="user_detail" direction="vertical" size={10} style={{ width: "100%" }}>
                    {rent?.user && <RentUserDetail user={rent?.user} />}
                </Space>
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    {rent?.additionalDriver && <RentUserDetail user={rent?.additionalDriver} additionalDriverMode={true} />}
                </Space>
                <Space id="car_detail" direction="vertical" size={10} style={{ width: "100%" }}>
                    {rent && <RentCarDetail rent={rent} onChangeRentBattery={onChangeRentBattery} />}
                </Space>
                <Space id="payment_detail" direction="vertical" size={10} style={{ width: "100%" }}>
                    {rent && <RentPaymentDetail rent={rent} onUpdateRent={() => onRefreshRentData()} />}
                </Space>
            </Space>
            {openModalCancelReservation && cancelRent && (
                <Modal
                    centered
                    title="예약취소"
                    open={openModalCancelReservation}
                    onOk={handleCancelReservationModelOk}
                    okText="예약취소"
                    onCancel={() => {
                        setOpenModalCancelReservation(false);
                    }}
                    cancelText="이전"
                >
                    {displayReturnPayment()}
                </Modal>
            )}
        </PageContainer>
    );
}

export default RentInfo;
